import { Ace as AceNamespace } from 'ace-builds';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/function';
import { useSelector } from 'react-redux';
import { match } from 'ts-pattern';

import { selectFeatureFlags } from 'core/userContext';
import { isMovableInk } from 'modules/movableInk/utils';
import { useModalContext } from 'utils/contexts/modal';
import { useSideBySideEditorContext } from '../contexts/SideBySideEditorContext';
import { Modals } from '../types';
import { getCurrentElement, getParentElementRange, isAnchorElement } from '../utils';
import { MutableRefObject } from 'react';

export enum ImageModalType {
  CreativeLibrary = 'creativeLibrary',
  MovableInk = 'movableInk',
}

const isMovableInkImage = (node: HTMLImageElement): boolean =>
  pipe(node.getAttribute('src'), O.fromNullable, O.filter(isMovableInk), O.isSome);

const getEditor = (
  editor?: MutableRefObject<AceNamespace.Editor | null>,
): O.Option<AceNamespace.Editor> =>
  pipe(
    editor,
    O.fromNullable,
    O.map((e) => e.current),
    O.flatMap(O.fromNullable),
  );

const getParentRange = (
  editor?: MutableRefObject<AceNamespace.Editor | null>,
  eltRange?: AceNamespace.Range,
): O.Option<AceNamespace.Range> =>
  pipe(
    getEditor(editor),
    O.map((e) => getParentElementRange(e, eltRange)),
    O.flatMap(O.fromNullable),
  );

const getMovableInkImage = (node?: HTMLImageElement): O.Option<HTMLImageElement> =>
  pipe(node, O.fromNullable, O.filter(isMovableInkImage));

const getParentElementFromRange = (
  editor: AceNamespace.Editor,
  parentRange: AceNamespace.Range,
): O.Option<HTMLAnchorElement> =>
  pipe(getCurrentElement(editor, parentRange), O.fromNullable, O.filter(isAnchorElement));

const getParentElement = (
  editor: O.Option<AceNamespace.Editor>,
  range: O.Option<AceNamespace.Range>,
) =>
  pipe(
    editor,
    O.flatMap((e) =>
      pipe(
        range,
        O.map((r) => [e, r] as [AceNamespace.Editor, AceNamespace.Range]),
      ),
    ),
    O.flatMap(([e, r]) => getParentElementFromRange(e, r)),
    O.toUndefined,
  );

const selectRange = (editor: AceNamespace.Editor, range?: AceNamespace.Range) => {
  // The native double-click behaviors need to finish before we can automatically set the
  // selection
  setTimeout(() => {
    editor.selection.setSelectionRange(range);
  }, 250);
};

const selectParent = (editor: O.Option<AceNamespace.Editor>, range?: AceNamespace.Range) =>
  pipe(
    editor,
    O.flatMap((e) => {
      selectRange(e, range);
      return O.none;
    }),
  );

const getModalToOpen = (
  modalType: ImageModalType,
  isMovableInkImageSelected: boolean,
  nothingSelected: boolean,
  isMovableInkEnabled: boolean,
) =>
  match(modalType)
    .when(
      (t) => t === ImageModalType.CreativeLibrary,
      () => Modals.CreativeLibrary,
    )
    .when(
      (t) => t === ImageModalType.MovableInk && isMovableInkEnabled,
      () => Modals.MovableInk,
    )
    .when(
      () => (isMovableInkImageSelected || nothingSelected) && isMovableInkEnabled,
      () => Modals.MovableInk,
    )
    .otherwise(() => Modals.CreativeLibrary);

export const useHandleImageModal = () => {
  const { editor: editorRef, setSelectedImage, setSelectedAnchor } = useSideBySideEditorContext();
  const { showModal } = useModalContext();
  const { MovableInk } = useSelector(selectFeatureFlags);

  return (
    eltRange?: AceNamespace.Range,
    node?: HTMLImageElement,
    modalType = ImageModalType.CreativeLibrary,
  ) => {
    setSelectedImage(node);
    const image = getMovableInkImage(node);
    const anchor = getParentRange(editorRef, eltRange);
    const editor = getEditor(editorRef);

    const nextElement = getParentElement(editor, anchor);
    setSelectedAnchor(nextElement);

    if (O.isSome(image)) {
      selectParent(editor, O.toUndefined(anchor));
    }

    const modalToOpen = getModalToOpen(modalType, O.isSome(image), !node, MovableInk);

    showModal(modalToOpen);
  };
};
